import { type ReactNode, type JSX } from 'react';

import { CheckmarkSmallIcon } from '@dynatrace/strato-icons';

import type {
  CodeComponent,
  Components,
} from '../../../core/types/markdown/react-markdown.js';
import { Divider } from '../../../layouts/divider/Divider.js';
import { Blockquote } from '../../../typography/block-quote/Blockquote.js';
import { Code } from '../../../typography/code/Code.js';
import { Emphasis } from '../../../typography/emphasis/Emphasis.js';
import { ExternalLink } from '../../../typography/external-link/ExternalLink.js';
import { Heading } from '../../../typography/heading/Heading.js';
import { List } from '../../../typography/list/List.js';
import { Paragraph } from '../../../typography/paragraph/Paragraph.js';
import { Strikethrough } from '../../../typography/strikethrough/Strikethrough.js';
import { Strong } from '../../../typography/strong/Strong.js';
import { Text } from '../../../typography/text/Text.js';
import { CodeSnippet } from '../../code-snippet/CodeSnippet.js';
import { matchLanguageParm } from '../utils.js';
import {
  MarkdownCheckmarkBackgroundCSS,
  MarkdownHeadingCSS,
  MarkdownImageCSS,
  MarkdownListCSS,
  MarkdownListItemCSS,
  MarkdownParagraphCSS,
} from './typography-mapping.sty.js';

type Language = Parameters<typeof CodeSnippet>[0]['language'];

/** The Code component used within Markdown. We need to switch between inline Code, CodeSnippet and CodeEditor component */
const MarkdownCode: CodeComponent = (props) => {
  const { className, inline, children } = props;
  const match = matchLanguageParm(className);

  // TODO: move this sanitization to a remark plugin
  let sanitizedChildren: ReactNode = children;
  // Remove trailing \n that is not removed by remark
  // Depending on the Markdown provider we need to check the children are string or an array to correctly pass them down.
  if (Array.isArray(children)) {
    const firstChild = children[0];

    sanitizedChildren = [
      typeof firstChild === 'string' ? firstChild.replace(/\n$/g, '') : '',
    ];
  } else if (typeof (children as unknown) === 'string') {
    sanitizedChildren = (children as string).replace(/\n$/g, '');
  }

  return inline ? (
    <Code data-testid="code">{sanitizedChildren}</Code>
  ) : (
    <CodeSnippet
      data-testid="code-snippet"
      language={match ? (match[1] as Language) : undefined}
    >
      <CodeSnippet.ActionsMenu>
        <CodeSnippet.Copy />
      </CodeSnippet.ActionsMenu>
      {sanitizedChildren}
    </CodeSnippet>
  );
};

export const typographyMappings: Components & {
  inlineCode: (props: Record<string, unknown>) => JSX.Element;
} = {
  code: MarkdownCode,
  // We need to add the inlineCode here since we use react-markdown in this repo
  // but mdx-js/react inside the cli-plugin
  inlineCode: ({ node, ...props }) => <Code {...props}></Code>,
  p: ({ children }) => (
    <Paragraph className={MarkdownParagraphCSS} data-testid="paragraph">
      {children}
    </Paragraph>
  ),
  strong: ({ ref, node, ...props }) => (
    <Strong data-testid="strong" {...props} />
  ),
  a: ({ ref, href, node, ...props }) => (
    <ExternalLink
      data-testid="external-link"
      href={href as string}
      {...props}
    />
  ),
  input: ({ checked }) => (
    <span
      className={MarkdownCheckmarkBackgroundCSS}
      role="checkbox"
      aria-label={checked ? 'checked' : 'unchecked'}
      aria-checked={!!checked}
      aria-disabled
    >
      {checked ? <CheckmarkSmallIcon /> : null}
    </span>
  ),
  ul: ({ children }) => <List className={MarkdownListCSS}>{children}</List>,
  ol: ({ children }) => (
    <List className={MarkdownListCSS} ordered>
      {children}
    </List>
  ),
  li: ({ ref, node, ordered, ...props }) => {
    return <Text {...props} className={MarkdownListItemCSS} />;
  },
  h1: ({ ref, node, ...props }) => (
    <Heading
      {...props}
      className={MarkdownHeadingCSS}
      data-testid="heading"
      level={1}
      as="h1"
    />
  ),
  h2: ({ ref, node, ...props }) => (
    <Heading
      {...props}
      className={MarkdownHeadingCSS}
      data-testid="heading"
      level={2}
      as="h2"
    />
  ),
  h3: ({ ref, node, ...props }) => (
    <Heading
      {...props}
      className={MarkdownHeadingCSS}
      data-testid="heading"
      level={3}
      as="h3"
    />
  ),
  h4: ({ ref, node, ...props }) => (
    <Heading
      {...props}
      className={MarkdownHeadingCSS}
      data-testid="heading"
      level={4}
      as="h4"
    />
  ),
  h5: ({ ref, node, ...props }) => (
    <Heading
      {...props}
      className={MarkdownHeadingCSS}
      data-testid="heading"
      level={5}
      as="h5"
    />
  ),
  h6: ({ ref, node, ...props }) => (
    <Heading
      {...props}
      className={MarkdownHeadingCSS}
      data-testid="heading"
      level={6}
      as="h6"
    />
  ),
  del: ({ ref, node, ...props }) => <Strikethrough {...props} />,
  em: ({ ref, node, ...props }) => <Emphasis {...props} />,
  blockquote: ({ ref, node, ...props }) => <Blockquote {...props} />,
  hr: () => <Divider orientation="horizontal" as="hr" />,
  img: ({ ref, alt, node, ...props }) => (
    <img {...props} alt={alt} className={MarkdownImageCSS} />
  ),
};
